<!-- Card Mode: Full detailed card using existing beautiful sub-components -->
@if (displayMode === 'card') {
  <mat-card class="unified-task-card" [class]="cachedStatusClass">
    <app-task-progress-header
      [taskConfig]="cachedTaskConfig"
      [overallProgress]="task.progress"
      [isRunning]="isActive()"
      [isSinglePage]="cachedIsSinglePage"
      [pageNumbers]="cachedPageNumbers"
      [firstPageNumber]="cachedFirstPageNumber"
      [taskId]="task.taskId"
      [isFailed]="isFailed()"
      [isCompleted]="isCompleted()"
      [isCancelled]="isCancelled()"
      [costAnalysis]="cachedCostAnalysis"
      [fileId]="cachedFileId"
      (dismissCard)="onDismissCard()"
      (openCostDetails)="onOpenCostDetails($event)"
    ></app-task-progress-header>
    <mat-card-content>
      @if (showTaskActions) {
        <app-task-actions
          [isRunning]="isActive()"
          [showStartButton]="showStartButton"
          (startTask)="onStartTask()"
          (cancelTasks)="onCancel()"
        ></app-task-actions>
      }
      @if (showProgressDetails) {
        <div class="progress-section">
          @if (!cachedHasStepHistory) {
            <app-task-progress-bar
              [overallProgress]="task.progress"
              [isRunning]="isActive()"
              [overallMessage]="task.message"
              [queuingElapsedTime]="cachedQueuingElapsedTime"
              [hasStepHistory]="cachedHasStepHistory"
              [startTime]="task.startTime || null"
              [endTime]="task.endTime || null"
              [elapsedTime]="cachedTaskElapsedTime"
              [isCompleted]="isCompleted()"
              [isFailed]="isFailed()"
              [isCancelled]="isCancelled()"
            ></app-task-progress-bar>
          }
          @if (showStepHistory && cachedHasStepHistory) {
            <mat-expansion-panel class="steps-panel" [(expanded)]="expandSteps">
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <div class="header-content">
                    <div class="header-top">
                      <mat-icon>work</mat-icon>
                      <span class="panel-title-text">
                        Task Progress
                        @if (task.startTime) {
                          <span class="timing-info">
                            | Started: {{ task.startTime | date: 'short' }} ({{ cachedTaskElapsedTime }})
                          </span>
                        }
                        @if (isCompleted() && task.endTime) {
                          <span class="completion-info"> | Completed: {{ task.endTime | date: 'short' }} </span>
                        }
                        @if (isFailed() && task.endTime) {
                          <span class="completion-info failed-info">
                            | Failed: {{ task.endTime | date: 'short' }}
                          </span>
                        }
                        @if (isCancelled() && task.cancelledAt) {
                          <span class="completion-info"> | Cancelled: {{ task.cancelledAt | date: 'short' }} </span>
                        }
                      </span>
                      <span class="overall-progress-info" [ngClass]="{ 'failed-progress': isFailed() }">
                        {{ task.progress }}%
                      </span>
                    </div>
                    <div class="header-progress">
                      <app-task-progress-bar
                        [overallProgress]="task.progress"
                        [isRunning]="isActive()"
                        [overallMessage]="task.message"
                        [queuingElapsedTime]="cachedQueuingElapsedTime"
                        [hasStepHistory]="cachedHasStepHistory"
                        [startTime]="task.startTime || null"
                        [endTime]="task.endTime || null"
                        [elapsedTime]="cachedTaskElapsedTime"
                        [isCompleted]="isCompleted()"
                        [isFailed]="isFailed()"
                        [isCancelled]="isCancelled()"
                      ></app-task-progress-bar>
                    </div>
                  </div>
                </mat-panel-title>
              </mat-expansion-panel-header>
              <div class="steps-content">
                <div class="steps-list">
                  @for (step of cachedStepHistory; track step.stepNumber) {
                    <div class="step-item" [class]="getStepStatusClass(step)">
                      <div class="step-icon">
                        <mat-icon [class]="getStepStatusClass(step)">
                          {{ getStepIcon(step) }}
                        </mat-icon>
                      </div>
                      <div class="step-content">
                        <span class="step-number">Step {{ step.stepNumber }}/{{ step.totalSteps }}:</span>
                        <span class="step-title">{{ step.description }}</span>
                      </div>
                      <div class="step-timing">{{ getStepElapsedTimeCached(step.stepNumber) }}</div>
                    </div>
                  }
                </div>
              </div>
            </mat-expansion-panel>
          }
        </div>
      }
      @if ((isCompleted() || isFailed()) && !showStepHistory) {
        <div class="completion-info">
          <p class="task-message">{{ task.message }}</p>
          <div class="task-timing">
            @if (task.duration) {
              <span class="duration">{{ formatTaskDuration() }}</span>
            }
            @if (task.endTime) {
              <span class="end-time">{{ task.endTime | date: 'short' }}</span>
            }
          </div>
        </div>
      }
    </mat-card-content>
    @if (isActive() || isFailed()) {
      <mat-card-actions>
        @if (isFailed()) {
          <button mat-button (click)="onRetry()" color="primary">
            <mat-icon>refresh</mat-icon>
            Retry
          </button>
        }
        @if (isActive()) {
          <button mat-button (click)="onCancel()" color="warn">
            <mat-icon>cancel</mat-icon>
            Cancel
          </button>
        }
      </mat-card-actions>
    }
  </mat-card>
}

<!-- List Mode: Compact list item for completed tasks -->
@if (displayMode === 'list') {
  <mat-list-item class="unified-task-list-item" [class]="cachedStatusClass">
    <mat-icon matListItemIcon [class]="cachedStatusClass">{{ cachedStatusIcon }}</mat-icon>
    <div matListItemTitle class="task-title">{{ task.filename }}</div>
    <div matListItemLine class="task-details">
      <mat-chip [class]="'status-chip-' + task.status" class="status-chip">
        {{ cachedStatusText }}
      </mat-chip>
      @if ((isCompleted() || isFailed() || isCancelled()) && task.duration) {
        <span class="task-duration">
          {{ formatTaskDuration() }}
        </span>
      }
      @if (isActive() && task.startTime) {
        <span class="task-duration">{{ cachedTaskElapsedTime }}</span>
      }
      <span class="task-time">{{ task.startTime | date: 'short' }}</span>
    </div>
    <div matListItemLine class="task-id-line">
      <span class="task-id">{{ task.taskId }}</span>
    </div>
    @if (isCompleted() && task.endTime) {
      <div matListItemLine class="task-timing-line">
        <span class="timing-info">Completed: {{ task.endTime | date: 'short' }}</span>
      </div>
    }
    @if (task.message) {
      <div matListItemLine class="task-message">{{ task.message }}</div>
    }
    @if (cachedHasCostAnalysis) {
      <div matListItemLine class="cost-analysis-line">
        <app-cost-analysis-display
          [costAnalysis]="cachedCostAnalysis"
          displayMode="summary"
        ></app-cost-analysis-display>
      </div>
    }
    <div class="task-actions" matListItemMeta>
      @if (isFailed()) {
        <button mat-icon-button (click)="onRetry()" matTooltip="Retry task" class="action-button retry-button">
          <mat-icon>refresh</mat-icon>
        </button>
      }
      @if (cachedHasProgressSteps) {
        <button
          mat-icon-button
          (click)="onToggleSteps()"
          matTooltip="View details"
          class="action-button details-button"
        >
          <mat-icon>info</mat-icon>
        </button>
      }
    </div>
  </mat-list-item>
}

<!-- Compact Mode: Minimal display for dense lists -->
@if (displayMode === 'compact') {
  <div class="unified-task-compact" [class]="cachedStatusClass">
    <mat-icon class="status-icon" [class]="cachedStatusClass">{{ cachedStatusIcon }}</mat-icon>
    <div class="task-info">
      <div class="task-name">{{ task.filename }}</div>
      <div class="task-status">
        <mat-chip [class]="'status-chip-' + task.status" size="small">{{ cachedStatusText }}</mat-chip>
        @if (isActive()) {
          <span class="task-progress">{{ task.progress }}%</span>
        }
      </div>
      <div class="task-timing-compact">
        @if (task.startTime) {
          <span class="start-time">{{ task.startTime | date: 'short' }}</span>
        }
        @if ((isCompleted() || isFailed() || isCancelled()) && task.duration) {
          <span class="duration">
            {{ formatTaskDuration() }}
          </span>
        }
        @if (isActive() && task.startTime) {
          <span class="elapsed">{{ cachedTaskElapsedTime }}</span>
        }
      </div>
      <div class="task-id-compact">
        <code>{{ task.taskId }}</code>
      </div>
      @if (cachedHasCostAnalysis) {
        <div class="cost-analysis-compact">
          <app-cost-analysis-display
            [costAnalysis]="cachedCostAnalysis"
            displayMode="compact"
          ></app-cost-analysis-display>
        </div>
      }
    </div>
    <div class="task-actions">
      @if (isFailed()) {
        <button mat-icon-button size="small" (click)="onRetry()">
          <mat-icon>refresh</mat-icon>
        </button>
      }
    </div>
  </div>
}
